import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";

export type ThemeMode = "light" | "dark" | "system";

/**
 * UI Store - manages global UI state
 */
export const useUiStore = defineStore("ui", () => {
  // Theme state
  const themeMode = ref<ThemeMode>("dark");
  const systemPrefersDark = ref(
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  // Sidebar state - default to closed on mobile
  const sidebarOpen = ref(window.innerWidth >= 768);
  const sidebarRail = ref(false);

  // Modal states
  const createPostModalOpen = ref(false);
  const settingsModalOpen = ref(false);

  // Loading states
  const globalLoading = ref(false);
  const loadingMessage = ref("");

  // Mobile detection
  const isMobile = ref(window.innerWidth < 768);

  // Getters
  const isDarkTheme = computed(() => {
    if (themeMode.value === "system") {
      return systemPrefersDark.value;
    }
    return themeMode.value === "dark";
  });

  const effectiveTheme = computed(() => (isDarkTheme.value ? "dark" : "light"));

  // Actions
  function setTheme(mode: ThemeMode): void {
    themeMode.value = mode;
    localStorage.setItem("theme-mode", mode);
  }

  function toggleTheme(): void {
    if (themeMode.value === "dark") {
      setTheme("light");
    } else if (themeMode.value === "light") {
      setTheme("dark");
    } else {
      // System mode - toggle based on current effective theme
      setTheme(isDarkTheme.value ? "light" : "dark");
    }
  }

  function initializeTheme(): void {
    // Load saved theme preference
    const savedTheme = localStorage.getItem("theme-mode") as ThemeMode | null;
    if (savedTheme && ["light", "dark", "system"].includes(savedTheme)) {
      themeMode.value = savedTheme;
    }

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", (e) => {
      systemPrefersDark.value = e.matches;
    });

    // Listen for window resize
    window.addEventListener("resize", () => {
      const wasMobile = isMobile.value;
      isMobile.value = window.innerWidth < 768;

      // Auto-close sidebar when transitioning to mobile
      if (!wasMobile && isMobile.value && sidebarOpen.value) {
        sidebarOpen.value = false;
      }
    });
  }

  function toggleSidebar(): void {
    sidebarOpen.value = !sidebarOpen.value;
  }

  function setSidebarOpen(open: boolean): void {
    sidebarOpen.value = open;
  }

  function setSidebarRail(rail: boolean): void {
    sidebarRail.value = rail;
  }

  function openCreatePostModal(): void {
    createPostModalOpen.value = true;
  }

  function closeCreatePostModal(): void {
    createPostModalOpen.value = false;
  }

  function openSettingsModal(): void {
    settingsModalOpen.value = true;
  }

  function closeSettingsModal(): void {
    settingsModalOpen.value = false;
  }

  function setGlobalLoading(loading: boolean, message: string = ""): void {
    globalLoading.value = loading;
    loadingMessage.value = message;
  }

  // Persist sidebar state
  watch(sidebarOpen, (open) => {
    localStorage.setItem("sidebar-open", String(open));
  });

  // Initialize sidebar from storage
  // On mobile (< 768px), always start closed per UI standards
  // On desktop, restore from localStorage
  function initializeSidebar(): void {
    if (isMobile.value) {
      // Mobile/PWA: always hidden by default
      sidebarOpen.value = false;
    } else {
      // Desktop: restore from localStorage if available
      const savedState = localStorage.getItem("sidebar-open");
      if (savedState !== null) {
        sidebarOpen.value = savedState === "true";
      }
    }
  }

  return {
    // State
    themeMode,
    systemPrefersDark,
    sidebarOpen,
    sidebarRail,
    createPostModalOpen,
    settingsModalOpen,
    globalLoading,
    loadingMessage,
    isMobile,

    // Getters
    isDarkTheme,
    effectiveTheme,

    // Actions
    setTheme,
    toggleTheme,
    initializeTheme,
    toggleSidebar,
    setSidebarOpen,
    setSidebarRail,
    openCreatePostModal,
    closeCreatePostModal,
    openSettingsModal,
    closeSettingsModal,
    setGlobalLoading,
    initializeSidebar,
  };
});

export type UiStore = ReturnType<typeof useUiStore>;
